


























































import i18n from "@/plugins/i18n";
import Vue, { VueConstructor } from "vue";
import { TranslateResult } from "vue-i18n";

export interface DialogOptions {
    //nothing yet
}

type ZenyaDialogHandlers = {
    ok?: () => Promise<boolean>;
    cancel?: () => Promise<boolean>;
};
/**
 * Dialog component
 *
 * Insert component where you want to use it:
 * <zenya-dialog ref="zenyadialog" v-model="showZenyaDialog"></zenya-dialog>
 *
 * Call it:
 *  (this.$refs.zenyadialog as Vue & IZenyaDialog).open().then((confirmed) => {})
 *
 *
 * Or use await:
 *  if (await (this.$refs.zenyadialog as Vue & IZenyaDialog).open()) {
 *    // ok
 *  }
 *  else {
 *    // cancel
 *  }
 *
 * The open() accept parameters that can be used to set the dialogs title and a message which is shown as default slot value.
 *  (this.$refs.zenyadialog as Vue & IZenyaDialog).open('Delete?', 'Are you sure you want to delete this?').then((confirmed) => {})
 *
 * For more complex stuff use v-model on dialog to show it and call registerHandlers(..) to set handlers for ok/cancel:
 *
 * <zenya-dialog v-model="showZenyaDialog">
 *  <dialog-content-component />
 * </zenya-dialog>
 *
 * import { ZenyaDialogComponent, ZenyaDialogInjects } from "../dialogs/ZenyaDialog.vue";
 * export default ZenyaDialogComponent.extend({
 *  inject: ZenyaDialogInjects,
 *  name: "DialogContentComponent"
 *  props: {
 *  }
 *  created() {
 *      //connect handlers to be called when ok/cancel is clicked in the dialog
 *      this.registerHandlers({ ok: this.onOk, cancel: this.onCancel });
 *  },
 *  methods: {
 *     async onOk() {
 *       await doStuff();
 *       return true;   //true will close the dialog, false keeps it open
 *   },
 *   async onCancel() {
 *       await cancelStuff();
 *       return true;   //true will close the dialog, false keeps it open
 *   }
 *  }
 * });

 */

export interface IZenyaDialog {
    /**
     * opens dialog with promise to be resolved when dialog is closed
     * @param options
     */
    open(title?: TranslateResult, message?: TranslateResult, options?: DialogOptions): Promise<boolean>;

    /**
     * close the dialog
     * @param result whether the dialog was closed with OK
     */
    close(result: boolean);
}

export interface IZenyaDialogComponent {
    /**
     * Register handler functions to be called on ok/cancel
     * @param handlers the handlers to register
     */
    registerHandlers(handlers: ZenyaDialogHandlers): void;

    /**
     * Unregister handlers
     */
    clearHandlers(): void;
}

/**
 * Export definition to create component hosted in dialog
 */
export const ZenyaDialogComponent = Vue as VueConstructor<Vue & IZenyaDialogComponent>;

//export the set of provided
export const ZenyaDialogInjects = ["registerHandlers", "clearHandlers"];

export default (Vue as VueConstructor<Vue & IZenyaDialog>).extend({
    name: "ZenyaDialog",
    components: {},
    props: {
        small: Boolean,
        large: Boolean,
        customWidth: Number,
        xLarge: Boolean,
        value: Boolean,
        scrollable: { default: false },
        title: String as () => TranslateResult,
        message: String as () => TranslateResult,
        noButtons: Boolean,
        transition: { default: () => "dialog-transition" },
        showOk: { default: () => true },
        showCancel: { default: () => true },
        okLabel: {
            default: () => {
                return i18n.t("general.buttons.ok");
            }
        },
        cancelLabel: {
            default: () => {
                return i18n.t("general.buttons.cancel");
            }
        },
        okId: { default: () => "ZenyaDialogOkButton" },
        cancelId: { default: () => "ZenyaDialogCancelButton" },
        autoMaxHeight: { default: false },
        clickOutside: { type: Function, default: null },
        keepContentOnClose: { type: Boolean, default: false }
    },
    provide: function () {
        return {
            registerHandlers: (handlers: ZenyaDialogHandlers) => {
                if (handlers.ok) this.handlers.ok = handlers.ok;
                if (handlers.cancel) this.handlers.cancel = handlers.cancel;
            },
            clearHandlers: () => {
                this.handlers.ok = null;
                this.handlers.cancel = null;
            }
        };
    },
    data: function () {
        return {
            openTitle: this.title,
            openMessage: this.message,
            resolve: null,
            reject: null,
            handlers: {
                ok: null,
                cancel: null
            } as ZenyaDialogHandlers,
            disabledOk: false,
            maxContentHeight: null as Number
        };
    },
    computed: {
        titleText: {
            get(): TranslateResult {
                return this.openTitle || this.title;
            }
        },
        messageText: {
            get(): TranslateResult {
                return this.openMessage || this.message;
            },
            set(value: TranslateResult) {
                this.openMessage = value;
                //emit updated message value to update message property value
                this.$emit("update:message", value);
            }
        },
        size() {
            if (this.customWidth) {
                return `${this.customWidth}px`;
            } else {
                return this.xLarge ? null : this.large ? "1000px" : this.small ? "400px" : "800px";
            }
        },
        fullscreen(): boolean {
            return this.$vuetify.breakpoint.smAndDown;
        },
        showCancelButtonAsText(): boolean {
            return this.showOk || this.$slots.customButtons !== undefined;
        }
    },
    watch: {
        fullscreen() {
            this.onResize();
        },
        openTitle() {
            this.$emit("update:title", this.openTitle);
        }
    },
    methods: {
        close(result: boolean) {
            //set data back to prop values
            this.openTitle = null;
            this.messageText = this.message;

            //emit input event to set value to false (v-model)
            this.$emit("input", false);

            //call resolve to continue awaiter
            if (this.resolve) {
                this.resolve(result);

                //unset resolve, it can only be done once
                this.resolve = null;
            }
        },

        open(title: TranslateResult, message: TranslateResult, options: DialogOptions): Promise<boolean> {
            if (title) {
                this.openTitle = title;
            }

            if (message) {
                this.messageText = message;
            }

            //emit input=true to set value to true (v-model)
            this.$emit("input", true);

            //return a promise that will be resolved on ok/cancel
            return new Promise<boolean>((resolve, reject) => {
                this.resolve = resolve;
                this.reject = reject;
            });
        },

        async ok() {
            this.disabledOk = true;
            let handlerResult: boolean = true;

            if (this.handlers.ok) {
                handlerResult = await this.handlers.ok();
            }

            this.disabledOk = false;

            if (handlerResult === true) {
                this.close(true);
                //emit ok event
                this.$emit("ok");
            }
        },

        async cancel() {
            //ensure cancel is only done when available
            if (!this.showCancel) return false;

            let handlerResult: boolean = true;

            if (this.handlers.cancel) {
                handlerResult = await this.handlers.cancel();
            }

            if (handlerResult === true) {
                this.close(false);
                //emit cancel event
                this.$emit("cancel");
            }
        },
        onResize() {
            //prevent null reference errors when $refs are not yet ready
            if (!this.$refs.cardTitle) return;

            const usedHeights = this.getTitleAndButtonsHeight();

            if (this.fullscreen) {
                this.maxContentHeight = window.innerHeight - usedHeights; //= calc(100% - ${usedHeights}px)`
            } else {
                this.maxContentHeight = 0.8 * window.innerHeight - usedHeights; //= calc(90% - ${usedHeights}px)
            }
        },
        getCardStyle(): Partial<CSSStyleDeclaration> {
            return {
                maxHeight: this.autoMaxHeight ? `${this.maxContentHeight}px` : `auto` //`calc(${dialogHeight} - ${usedHeights}px)`
            };
        },
        getTitleAndButtonsHeight(): number {
            if (!this.$refs.cardTitle) return;
            let cardTextStyle = window.getComputedStyle(this.$refs.cardText as Element);
            let contentPadding = parseFloat(cardTextStyle.paddingTop) + parseFloat(cardTextStyle.paddingBottom);
            return contentPadding + (this.$refs.cardTitle as Element)?.scrollHeight + (this.$refs.cardActions as Element)?.scrollHeight;
        }
    }
});
